import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppState } from '../../app/state';
import { callAction } from '../../app/actions';
import { isTagVisible, tagChildren, tagCount } from '../../lib/tags';
import { buildHiddenSets, canEditTags, isPermHidden } from '../../lib/permissions';
import { inEditMode } from '../../lib/mode';
import { toHex6 } from '../../lib/color';
import type { TagId } from '../../lib/types';

/**
 * The tag tree — since plan 2.3 a CHILD COMPONENT of the topbar's tags dropdown
 * (`TopbarSearch`'s `#tag-panel`), no longer a standalone sidebar region. The `host` prop is
 * the panel's body div (`#tag-tree`), which is still the drop surface for "move to top level".
 *
 * The first region that READS map state rather than only rendering its own, which makes it
 * the real test of the bridge — and the first to use the lifted pure core in production
 * rather than only in tests: `tagChildren`, `tagCount`, `isTagVisible` and `toHex6` are the
 * same functions `e2e/15-core-parity.spec.ts` proved identical to the originals.
 *
 * WHAT IT DOES NOT OWN. Every mutation still runs in the vanilla script, reached through a
 * named action. `deleteTag` alone is 45 lines of undo snapshots, child-first server ordering
 * and node-assignment restoration; reimplementing that to move a tree view would be a large
 * risk for no gain. React owns rendering and interaction; the vanilla side keeps the
 * mutations, and takes care of its own re-render, save and sync afterwards. Those move later,
 * when the state layer does.
 *
 * The one thing extracted from vanilla is the inline rename input, because that is rendering:
 * `renameTag()` replaced a span with an input and wired blur/Enter/Escape by hand. React owns
 * the field and calls `setTagName` with the result.
 */

const EMPTY_MESSAGE =
  'No tags yet. Add one below. Click a tag to highlight (and reveal) its nodes. In Edit mode: ' +
  'double-click to rename, use the colour dot to recolour, and drag rows to reorder or nest them.';

/** Where a dragged row would land relative to the one under the pointer. */
type DropPos = 'before' | 'inside' | 'after';

/** How long a single click waits for a possible double-click, in edit mode only. */
const RENAME_GRACE_MS = 240;

function EyeIcon({ open }: { open: boolean }) {
  // Mirrors eyeSvg() in the vanilla page, which points at the same two inline <symbol> defs.
  return (
    <svg className="ico" style={{ width: 16, height: 16 }}>
      <use href={open ? '#i-eye' : '#i-eye-off'} />
    </svg>
  );
}

export function TagTree({ host }: { host: HTMLElement }) {
  const s = useAppState();
  const [renaming, setRenaming] = useState<TagId | null>(null);
  const [dragging, setDragging] = useState<TagId | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: TagId; pos: DropPos } | null>(null);
  // The click/double-click race: a single click filters, but only after waiting to see
  // whether a second one arrives to start a rename instead.
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (clickTimer.current) clearTimeout(clickTimer.current);
    };
  }, []);

  /**
   * Drop onto EMPTY space in the tree to move a tag back to the top level.
   *
   * Attached to the host rather than rendered, because the host is the drop surface and React
   * only owns what is inside it. `e.target === host` is what distinguishes empty space from a
   * row — a row handles its own drop and this must not also fire.
   *
   * THAT TEST IS LOAD-BEARING IN `onDrop`, AND USED NOT TO BE THERE (V0.49.3). The vanilla
   * page relied on the row's own drop handler calling `stopPropagation()`, and it worked:
   * both handlers were native listeners, the row's was on a DESCENDANT of this host, and
   * stopping propagation there genuinely kept the event from ever reaching this one. A React
   * synthetic handler cannot do the same thing. React dispatches at its ROOT CONTAINER —
   * `#tb-center`, an ANCESTOR of `#tag-tree` — so a drop on a row bubbles through this
   * listener FIRST, and this listener moved the tag to the top level before the row's
   * `onDrop` had run at all. Dragging a tag onto another one un-nested it instead of nesting
   * it, and reordering was lost the same way. The fix is to stop depending on propagation
   * order across the React boundary and ask the same question `onOver` already asks.
   *
   * Easy to miss when porting: it lived a hundred lines away from the tree rendering, in its
   * own IIFE next to the sidebar controls, and nothing else referenced it.
   */
  useEffect(() => {
    if (!dragging) return;

    const onOver = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      if (e.target === host) {
        setDropTarget(null);
        host.classList.add('drop-root');
      } else {
        host.classList.remove('drop-root');
      }
    };
    const onDrop = (e: DragEvent) => {
      // EMPTY SPACE ONLY — the same question `onOver` asks to light `drop-root`, and for the
      // same reason. Anything else is a row's drop and belongs to the row (see above).
      if (e.target !== host) return;
      e.preventDefault();
      host.classList.remove('drop-root');
      const drag = dragging;
      setDragging(null);
      setDropTarget(null);
      if (drag) callAction('moveTag', drag, null, 'inside');
    };

    host.addEventListener('dragover', onOver);
    host.addEventListener('drop', onDrop);
    return () => {
      host.removeEventListener('dragover', onOver);
      host.removeEventListener('drop', onDrop);
      host.classList.remove('drop-root');
    };
  }, [dragging, host]);

  // Tag STRUCTURE — rename, drag, add, recolour, invisibility — is its own brick (V0.37).
  const editable = inEditMode(s) && canEditTags(s.perm);
  const hidden = buildHiddenSets(s.perm, s.tagEdges);
  const nodeHidden = useCallback(
    (id: string) => isPermHidden(id, s.edges, hidden),
    // `hidden` is rebuilt each render from the same inputs; keying on the payload avoids a
    // new identity every time without having to memo the sets themselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [s.edges, s.perm],
  );

  const cancelPendingClick = () => {
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }
  };

  const toggleFilter = (tid: TagId) => {
    // Multi-select (plan 2.3): a click adds or removes THIS tag; the others stay selected.
    callAction('toggleActiveTag', tid);
  };

  const onRowClick = (tid: TagId) => {
    // In edit mode a double-click renames, so hold the filter briefly. In view mode there is
    // no rename, so react immediately — waiting would just feel laggy.
    if (!editable) {
      toggleFilter(tid);
      return;
    }
    cancelPendingClick();
    clickTimer.current = setTimeout(() => {
      clickTimer.current = null;
      toggleFilter(tid);
    }, RENAME_GRACE_MS);
  };

  const roots = tagChildren(s.tags, s.tagEdges, null);
  if (!roots.length) {
    return <div style={{ fontSize: 12.5, color: 'var(--ink-faint)', padding: '10px 8px' }}>{EMPTY_MESSAGE}</div>;
  }

  const rows: React.ReactNode[] = [];

  const pushRow = (tid: TagId, depth: number) => {
    const t = s.tags[tid];
    if (!isTagVisible(t, tid, { hiddenTags: hidden.tags, editing: inEditMode(s), canEdit: canEditTags(s.perm) })) {
      return;
    }
    const kids = tagChildren(s.tags, s.tagEdges, tid);
    const isDropTarget = dropTarget?.id === tid;
    const dropClass = isDropTarget
      ? dropTarget.pos === 'inside'
        ? ' drop-into'
        : dropTarget.pos === 'before'
          ? ' drop-before'
          : ' drop-after'
      : '';

    rows.push(
      <div
        key={tid}
        className={`tagrow${s.activeTags.has(tid) ? ' active' : ''}${dragging === tid ? ' dragging' : ''}${dropClass}`}
        style={{ paddingLeft: 6 + depth * 14 }}
        data-tag={tid}
        onClick={() => onRowClick(tid)}
        // Text selection inside the rename field would otherwise start a row drag.
        draggable={editable && renaming !== tid}
        onDragStart={(e) => {
          setDragging(tid);
          e.dataTransfer.effectAllowed = 'move';
          try {
            e.dataTransfer.setData('text/plain', tid);
          } catch {
            /* Safari refuses setData in some contexts; the drag still works via state */
          }
        }}
        onDragEnd={() => {
          setDragging(null);
          setDropTarget(null);
        }}
        onDragOver={(e) => {
          if (!dragging || dragging === tid) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          // The same thirds as the original: the outer bands reorder, the middle nests.
          const r = e.currentTarget.getBoundingClientRect();
          const y = e.clientY - r.top;
          const pos: DropPos = y < r.height * 0.28 ? 'before' : y > r.height * 0.72 ? 'after' : 'inside';
          setDropTarget({ id: tid, pos });
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const pos = dropTarget?.id === tid ? dropTarget.pos : 'inside';
          const drag = dragging;
          setDropTarget(null);
          setDragging(null);
          if (drag && drag !== tid) callAction('moveTag', drag, tid, pos);
        }}
      >
        <span
          className="caret"
          onClick={(e) => {
            e.stopPropagation();
            callAction('toggleTagCollapsed', tid);
          }}
        >
          {kids.length ? (t.collapsed ? '▶' : '▼') : ''}
        </span>

        {editable ? (
          <input
            type="color"
            className="dot"
            value={toHex6(t.color)}
            title="Change colour"
            onClick={(e) => e.stopPropagation()}
            // Live preview on the nodes while dragging the picker, committed on change —
            // only the commit records an undo entry, or every intermediate hue would.
            onInput={(e) => callAction('previewTagColor', tid, (e.target as HTMLInputElement).value)}
            onChange={(e) => callAction('setTagColor', tid, e.target.value)}
          />
        ) : (
          <span className="dot" style={{ background: t.color, cursor: 'default' }} />
        )}

        {renaming === tid ? (
          <RenameField
            initial={t.name}
            onCommit={(name) => {
              setRenaming(null);
              callAction('setTagName', tid, name);
            }}
            onCancel={() => setRenaming(null)}
          />
        ) : (
          <span
            className="name"
            onDoubleClick={
              editable
                ? (e) => {
                    e.stopPropagation();
                    cancelPendingClick(); // the rename wins over the pending filter toggle
                    setRenaming(tid);
                  }
                : undefined
            }
          >
            {t.name}
          </span>
        )}

        <span className="count">{tagCount(tid, s.nodes, nodeHidden)}</span>

        {editable ? (
          <>
            <span
              className="row-x"
              title="Add sub-tag"
              style={{ fontSize: 16 }}
              onClick={(e) => {
                e.stopPropagation();
                callAction('addSubTag', tid);
              }}
            >
              +
            </span>
            <span
              className="row-x"
              title={
                t.invisible
                  ? 'Hidden from non-admins in view mode — click to make visible'
                  : 'Click to hide from non-admins in view mode'
              }
              onClick={(e) => {
                e.stopPropagation();
                callAction('toggleTagInvisible', tid);
              }}
            >
              <EyeIcon open={!t.invisible} />
            </span>
            <span
              className="row-x"
              title="Delete tag"
              onClick={(e) => {
                e.stopPropagation();
                callAction('deleteTag', tid);
              }}
            >
              ×
            </span>
          </>
        ) : null}
      </div>,
    );

    if (!t.collapsed) for (const k of kids) pushRow(k, depth + 1);
  };

  for (const r of roots) pushRow(r, 0);
  return <>{rows}</>;
}

/**
 * The inline rename field.
 *
 * Uncontrolled on purpose: the value only matters when the edit ends, and a controlled input
 * here would re-render the whole tree on every keystroke — `useAppState` puts this
 * component on the same update path as the map.
 */
function RenameField({
  initial,
  onCommit,
  onCancel,
}: {
  initial: string;
  onCommit: (name: string) => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  // Escape reverts, and blur fires immediately afterwards. Without this the blur handler
  // would commit the discarded text — harmlessly today, because the vanilla mutation guards
  // on the name actually changing, but it is a stray write across the seam either way.
  const cancelled = useRef(false);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  return (
    <input
      ref={ref}
      defaultValue={initial}
      // Inline rather than a class, matching the original: this field exists for a few
      // seconds at a time and never appeared in the stylesheet.
      style={{
        flex: 1,
        background: 'var(--panel-2)',
        border: '1px solid var(--accent)',
        color: 'var(--ink)',
        borderRadius: 6,
        padding: '2px 6px',
        font: 'inherit',
        fontSize: 13,
        outline: 'none',
      }}
      onClick={(e) => e.stopPropagation()}
      onBlur={(e) => {
        if (cancelled.current) return;
        onCommit(e.target.value);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          cancelled.current = true;
          onCancel();
          e.currentTarget.blur();
        }
      }}
    />
  );
}
